/**
 * Livi lesson review (Learning Loop 1.3) — super-admin only.
 *
 * Backed by LivisiblyLessonReviewApi (livisibly_lesson_review Lambda). Firms are
 * addressed by their opaque tenant key (content_key, "ck_…") in URLs — never the
 * customer id, which can be an email.
 */
import { get, patch, post } from 'aws-amplify/api';
import useSWR, { mutate } from 'swr';
import { processResponse } from './api_util';

const API_NAME = 'LivisiblyLessonReviewApi';

const KEY_PROPOSALS = 'LESSON_PROPOSALS';
const KEY_PROPOSAL = 'LESSON_PROPOSAL';
const KEY_FIRMS = 'LESSON_FIRMS';
const KEY_FIRM_LESSONS = 'LESSON_FIRM_LESSONS';
const KEY_HISTORY = 'LESSON_HISTORY';
const KEY_CENTRAL = 'LESSON_CENTRAL';

async function call(method, path, options) {
  const op = method({ apiName: API_NAME, path, ...(options ? { options } : {}) });
  return processResponse(await op.response);
}

/**
 * The backend's error for a failed call: { status, message, errorCode, details }.
 * `details` carries structured extras (e.g. `tier_index` for a full budget,
 * `scrub` for identifiers found). Falls back to a generic message.
 */
export function apiErrorDetails(error, fallback = 'Something went wrong. Please try again.') {
  const resp = error?._response || error?.response;
  let parsed = null;
  try {
    const body = resp?.body;
    parsed = typeof body === 'string' ? JSON.parse(body) : body || null;
  } catch (e) {
    parsed = null;
  }
  const { message, error_code: errorCode, ...details } = parsed || {};
  return {
    status: resp?.statusCode || null,
    message: message || fallback,
    errorCode: errorCode || null,
    details,
  };
}

// ── Reads (SWR) ──────────────────────────────────────────────────────────────

/** The review queue lists at most this many proposals per status (the nav badge shows "100+" past it). */
export const PROPOSAL_LIST_LIMIT = 100;

export function useLessonProposals(status, shouldFetch = true, swrOptions = undefined) {
  return useSWR(shouldFetch ? [KEY_PROPOSALS, status] : null, ([, s]) =>
    call(get, 'lessons/proposals', { queryParams: { status: s, limit: String(PROPOSAL_LIST_LIMIT) } }),
    swrOptions,
  );
}

/** While a proposal's tests run (Learning Loop 2.2) the open drawer re-reads it this often. */
export const TEST_POLL_MS = 10 * 1000;

/** A run still marked "running" after this long has stalled: stop polling and offer to run again. Each scenario
 * has a 15-minute budget and the tester runs two at a time across every proposal, so a 3-scenario run that waits
 * behind another proposal's can legitimately take ~45 minutes. */
export const TEST_RUN_STALE_MS = 60 * 60 * 1000;

/** Poll only while the latest read says a test run is in progress (and hasn't stalled). */
export function testPollInterval(latest, now = Date.now()) {
  const report = latest?.proposal?.eval_report;
  if (report?.status !== 'running') return 0;
  const started = Date.parse(report.requested_at || '');
  return Number.isFinite(started) && now - started > TEST_RUN_STALE_MS ? 0 : TEST_POLL_MS;
}

export function useLessonProposal(lessonProposalId, shouldFetch = true) {
  return useSWR(shouldFetch && lessonProposalId ? [KEY_PROPOSAL, lessonProposalId] : null, ([, id]) =>
    call(get, `lessons/proposals/${id}`),
    { refreshInterval: (latest) => testPollInterval(latest) },
  );
}

export function useLessonFirms(shouldFetch = true) {
  return useSWR(shouldFetch ? KEY_FIRMS : null, () => call(get, 'lessons/firms'));
}

export function useFirmLessons(contentKey, shouldFetch = true) {
  return useSWR(shouldFetch && contentKey ? [KEY_FIRM_LESSONS, contentKey] : null, ([, ck]) =>
    call(get, `lessons/firms/${ck}/lessons`),
  );
}

export function useLessonHistory(contentKey, lessonId, shouldFetch = true) {
  return useSWR(
    shouldFetch && contentKey && lessonId ? [KEY_HISTORY, contentKey, lessonId] : null,
    ([, ck, id]) => call(get, `lessons/firms/${ck}/lessons/${id}`),
  );
}

export function useCentralLessons(shouldFetch = true) {
  return useSWR(shouldFetch ? KEY_CENTRAL : null, () => call(get, 'lessons/central'));
}

// ── Writes ───────────────────────────────────────────────────────────────────

function refreshQueue() {
  return mutate((key) => Array.isArray(key) && [KEY_PROPOSALS, KEY_PROPOSAL].includes(key[0]));
}

function refreshFirms() {
  return mutate(
    (key) => key === KEY_FIRMS || (Array.isArray(key) && [KEY_FIRM_LESSONS, KEY_HISTORY].includes(key[0])),
  );
}

/** Reviewer edit → a new revision. `expectedRevision` is the revision on screen. */
export async function editProposal(lessonProposalId, fields, expectedRevision, note) {
  const result = await call(patch, `lessons/proposals/${lessonProposalId}`, {
    body: { ...fields, expected_revision: expectedRevision, ...(note ? { note } : {}) },
  });
  await refreshQueue();
  return result;
}

/** "Run tests" / "Run tests again": a fresh test run of the revision on screen (a newer run replaces an older one). */
export async function runProposalTests(lessonProposalId, expectedRevision) {
  const result = await call(post, `lessons/proposals/${lessonProposalId}/test`, {
    body: { expected_revision: expectedRevision },
  });
  await refreshQueue();
  return result;
}

/**
 * Firm lesson → live now; central lesson → staged for the next release.
 * `testOverrideReason`: a firm lesson whose tests didn't pass (or haven't run) is approved only on the reviewer's
 * stated reason (kept with the approval); a Livisibly-wide lesson can't be.
 */
export async function approveProposal(lessonProposalId, expectedRevision, { mandatory, testOverrideReason } = {}) {
  const body = { expected_revision: expectedRevision };
  if (mandatory !== undefined) body.mandatory = mandatory;
  if (testOverrideReason) body.test_override_reason = testOverrideReason;
  const result = await call(post, `lessons/proposals/${lessonProposalId}/approve`, { body });
  await Promise.all([refreshQueue(), refreshFirms(), mutate(KEY_CENTRAL)]);
  return result;
}

export async function rejectProposal(lessonProposalId, reason) {
  const result = await call(post, `lessons/proposals/${lessonProposalId}/reject`, { body: { reason } });
  await refreshQueue();
  return result;
}

/**
 * Move a firm lesson's live pointer: roll back / restore to `toVersion`, or
 * retire it (`toVersion` null). `expectedLiveVersion` is the version on screen.
 */
export async function moveLessonPointer(contentKey, lessonId, { toVersion, expectedLiveVersion, reason }) {
  const result = await call(post, `lessons/firms/${contentKey}/lessons/${lessonId}/pointer`, {
    body: { to_version: toVersion, expected_live_version: expectedLiveVersion, reason },
  });
  await refreshFirms();
  return result;
}
